/* eslint-disable @typescript-eslint/no-explicit-any */
type EventHandler<T> = (data: T) => Promise<void> | void;

interface EventSubscription {
    unsubscribe: () => void;
}

type EventMap = Record<string, unknown>;

export class EventBus<TEvents extends EventMap> {
    private eventHandlers: Map<keyof TEvents, Set<EventHandler<any>>> = new Map();
    private eventQueue: Array<{ type: keyof TEvents; data: any }> = [];
    private isProcessing: boolean = false;
    private delay: number = 0;

    constructor(config?: { delay?: number }) {
        this.delay = config?.delay ?? 0;
    }

    public subscribe<K extends keyof TEvents>(
        eventType: K,
        handler: EventHandler<TEvents[K]>
    ): EventSubscription {
        if (!this.eventHandlers.has(eventType)) {
            this.eventHandlers.set(eventType, new Set());
        }

        const handlers = this.eventHandlers.get(eventType)!;
        handlers.add(handler);

        return {
            unsubscribe: () => {
                handlers.delete(handler);
                if (handlers.size === 0) {
                    this.eventHandlers.delete(eventType);
                }
            },
        };
    }

    public publish<K extends keyof TEvents>(
        eventType: K,
        data: TEvents[K]
    ) {
        this.eventQueue.push({ type: eventType, data });
        this.startProcessing();
    }

    private startProcessing(): void {
        if (!this.isProcessing) {
            this.processQueue().catch(console.error);
        }
    }

    private async processQueue(): Promise<void> {
        if (this.isProcessing) return;
        this.isProcessing = true;

        try {
            while (this.eventQueue.length > 0) {
                const event = this.eventQueue.shift()!;

                if (this.delay > 0) {
                    await new Promise((resolve) => setTimeout(resolve, this.delay));
                }

                const handlers = [...(this.eventHandlers.get(event.type) ?? [])];

                if (handlers.length > 0) {
                    const promises = handlers.map((handler) =>
                        Promise.resolve(handler(event.data)).catch(console.error)
                    );
                    await Promise.all(promises);
                }
            }
        } finally {
            this.isProcessing = false;
            if (this.eventQueue.length > 0) {
                this.startProcessing();
            }
        }
    }

    public clearAllSubscriptions(): void {
        this.eventHandlers.clear();
    }
}
